// bd-deepcontext-b4zw — date/duration rendering lifted out of ScheduledJobs.tsx.
// Pure, so the strings the page and the runs modal show are unit-testable
// (see __tests__/scheduledJobsFormatters.test.ts).

export const formatDate = (
  dateString: string,
  showTimezone: boolean = true,
): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: showTimezone ? "short" : undefined,
  };

  return date.toLocaleString("en-US", options);
};

export const formatDateUTC = (dateString: string): string => {
  const date = new Date(dateString);
  return `${date.toISOString().replace("T", " ").slice(0, -5)} UTC`;
};

// bd-2c2pk — compact "Mon D" date in UTC. Year is appended only when it
// differs from the current year (active windows can cross years).
export const shortDate = (dateString: string): string => {
  const date = new Date(dateString);
  const base = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const year = date.getUTCFullYear();
  return year === new Date().getUTCFullYear() ? base : `${base} ${year}`;
};

// bd-2c2pk — deterministic relative time computed against the render-time
// clock. Past: "26m ago" form (no "ago" suffix here — caller adds it);
// future: "in 34m". Returns just the magnitude+unit; callers phrase it.
const relParts = (dateString: string): { value: number; unit: string } => {
  const diffMs = new Date(dateString).getTime() - Date.now();
  const absSec = Math.round(Math.abs(diffMs) / 1000);
  if (absSec < 60) return { value: absSec, unit: "s" };
  const absMin = Math.round(absSec / 60);
  if (absMin < 60) return { value: absMin, unit: "m" };
  const absHr = Math.round(absMin / 60);
  if (absHr < 24) return { value: absHr, unit: "h" };
  const absDay = Math.round(absHr / 24);
  return { value: absDay, unit: "d" };
};

// Past form, e.g. "26m" (caller wraps as "ran 26m ago").
export const rel = (dateString: string): string => {
  const { value, unit } = relParts(dateString);
  return `${value}${unit}`;
};

// Future form, e.g. "in 34m".
export const relFuture = (dateString: string): string => {
  const { value, unit } = relParts(dateString);
  return `in ${value}${unit}`;
};

export const calculateDuration = (
  startedAt?: string,
  completedAt?: string,
): string => {
  if (!startedAt || !completedAt) return "N/A";

  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;

  if (durationMs < 0) return "N/A";

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
    return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${seconds}s`;
  }
};
